import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import * as THREE from 'three';

function AnimatedParticles({ count = 2000 }) {
  const points = useRef();
  const light = useRef();

  const particles = useMemo(() => {
    const temp = [];
    for (let i = 0; i < count; i++) {
      const time = Math.random() * 100;
      const factor = 20 + Math.random() * 100;
      const speed = 0.01 + Math.random() / 200;
      const x = Math.sin((i / count) * Math.PI * 2) * factor;
      const y = Math.cos((i / count) * Math.PI * 2) * factor;
      const z = (Math.random() - 0.5) * 20;
      temp.push({ time, factor, speed, x, y, z });
    }
    return temp;
  }, [count]);

  // Initialize positions array
  const positions = useMemo(() => {
    const positions = new Float32Array(count * 3);
    particles.forEach((particle, i) => {
      positions[i * 3] = particle.x;
      positions[i * 3 + 1] = particle.y;
      positions[i * 3 + 2] = particle.z;
    });
    return positions;
  }, [particles, count]);

  useFrame((state) => {
    if (!points.current) return;
    
    const positions = points.current.geometry.attributes.position.array;
    
    particles.forEach((particle, i) => {
      let { factor, speed, x, y, z } = particle;
      const t = (particle.time += speed);
      
      // Calculate new position
      const newX = x + Math.cos((t / 10) * factor) + (Math.sin(t * 1) * factor) / 10;
      const newY = y + Math.sin((t / 10) * factor) + (Math.cos(t * 2) * factor) / 10;
      const newZ = z + Math.cos((t / 10) * factor) + (Math.sin(t * 3) * factor) / 10;
      
      // Update positions array
      positions[i * 3] = newX;
      positions[i * 3 + 1] = newY;
      positions[i * 3 + 2] = newZ;
    });
    
    // Mark positions as needing update
    points.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <>
      <pointLight ref={light} distance={40} intensity={8} color="#0B469D" />
      <Points ref={points} positions={positions} stride={3} frustumCulled={false}>
        <PointMaterial
          transparent
          color="#72a9fb"
          size={0.15}
          sizeAttenuation={true}
          depthWrite={false}
          opacity={0.7}
        />
      </Points>
    </>
  );
}

export default function ThreeBackground() {
  return (
    <div className="fixed inset-0 -z-10">
      <Canvas
        camera={{ position: [0, 0, 1], fov: 75 }}
        style={{ background: 'transparent' }}
        gl={{ alpha: true, antialias: true }}
      >
        <AnimatedParticles count={2000} />
      </Canvas>
    </div>
  );
}
